import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HotTable } from '@handsontable/react';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.min.css';
import type { ContainerMasterCategory } from '../utils/containerDispatchMaster';
import {
  TWO_COL_ITEMS_PER_ROW as ITEMS_PER_ROW,
  TWO_COL_SLOT_COLS as SLOT_COLS,
  displayItemNumber,
  expandItemsForNewVisualRow,
  gridToSequentialItems,
  isNumberCol,
  itemsToTwoColumnGrid,
  memoColInSlot as memoCol,
  remapIdsColumnMajor,
  sequentialIdxFromVisual,
  slotFromCol,
  valueColInSlot as valueCol,
} from '../utils/twoColumnGridLayout';

const MIN_ROWS = 8;
const GRID_COL_COUNT = ITEMS_PER_ROW * SLOT_COLS;

function buildHeaders(valueHeader: string): string[] {
  return Array.from({ length: ITEMS_PER_ROW }, () => ['No.', valueHeader, '비고']).flat();
}

type MasterItem = { id: string | null; value: string; remarks: string };

function emptyMasterItem(): MasterItem {
  return { id: null, value: '', remarks: '' };
}

function itemNumberRenderer(
  this: unknown,
  instance: Handsontable,
  td: HTMLTableCellElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties: Handsontable.CellProperties,
) {
  Handsontable.renderers.TextRenderer.call(
    this,
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties,
  );
  const slot = Math.floor(col / SLOT_COLS);
  const visualRowCount = instance.countRows();
  td.textContent = String(displayItemNumber(row, slot, visualRowCount));
  td.style.textAlign = 'center';
  td.style.color = '#546e7a';
  td.style.fontWeight = '600';
  td.style.background = '#f5f7f9';
}

function padItems(items: MasterItem[], minCount = MIN_ROWS): MasterItem[] {
  const next = [...items];
  while (next.length < minCount) {
    next.push(emptyMasterItem());
  }
  if (next.length % ITEMS_PER_ROW !== 0) {
    next.push(emptyMasterItem());
  }
  return next;
}

function itemsToGridData(items: MasterItem[]): string[][] {
  return itemsToTwoColumnGrid(
    items,
    MIN_ROWS,
    emptyMasterItem,
    item => [item.value, item.remarks],
  );
}

function itemIdsFromItems(items: MasterItem[]): (string | null)[] {
  return padItems(items).map(item => item.id);
}

function flattenGridToItems(data: string[][], ids: (string | null)[]): MasterItem[] {
  return gridToSequentialItems(data, ids, (id, value, remarks) => ({
    id,
    value,
    remarks,
  }));
}

interface Props {
  category: ContainerMasterCategory;
  valueHeader: string;
}

const ContainerMasterGrid: React.FC<Props> = ({ category, valueHeader }) => {
  const hotRef = useRef<any>(null);
  const itemIdsRef = useRef<(string | null)[]>([]);
  const deletedIdsRef = useRef<Set<string>>(new Set());
  const [tableData, setTableData] = useState<string[][]>([]);
  const [itemIds, setItemIds] = useState<(string | null)[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadRows = useCallback(async () => {
    setLoading(true);
    deletedIdsRef.current.clear();
    try {
      const res = await fetch(`/api/container-dispatch/master/${category}`);
      if (!res.ok) throw new Error('목록을 불러오지 못했습니다.');
      const rows: { id: string; value: string; remarks?: string | null }[] = await res.json();
      const items: MasterItem[] = rows.map(r => ({
        id: r.id,
        value: r.value || '',
        remarks: r.remarks || '',
      }));
      const ids = itemIdsFromItems(items);
      itemIdsRef.current = ids;
      setItemIds(ids);
      setTableData(itemsToGridData(items));
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : '조회 실패');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  useEffect(() => {
    itemIdsRef.current = itemIds;
  }, [itemIds]);

  const syncTableData = () => {
    setTableData(hotRef.current?.hotInstance?.getData() || []);
  };

  const handleSave = async () => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const data: string[][] = hot.getData();

    const deleteIds = new Set<string>(deletedIdsRef.current);
    const upsertRows: Array<{ id?: string; value: string; remarks: string | null; sortOrder: number }> = [];
    const items = flattenGridToItems(data, itemIds);

    let sortOrder = 0;
    items.forEach(item => {
      const id = item.id || undefined;
      const value = item.value.trim();
      const remarks = item.remarks.trim() || null;

      if (id && !value) {
        deleteIds.add(id);
        return;
      }
      if (!value) return;

      upsertRows.push({ id, value, remarks, sortOrder: sortOrder++ });
    });

    if (upsertRows.length === 0 && deleteIds.size === 0) {
      alert('저장할 변경사항이 없습니다.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      for (const id of deleteIds) {
        const res = await fetch(`/api/container-dispatch/master/rows/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('삭제 실패');
      }
      for (const row of upsertRows) {
        const res = await fetch(`/api/container-dispatch/master/${category}/rows`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(row),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || '저장 실패');
      }
      deletedIdsRef.current.clear();
      setMessage('저장되었습니다.');
      await loadRows();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRow = () => {
    const hot = hotRef.current?.hotInstance;
    const data: string[][] = hot ? hot.getData() : tableData;
    const oldVisualRowCount = data.length;
    const items = flattenGridToItems(data, itemIdsRef.current);
    const expanded = expandItemsForNewVisualRow(items, oldVisualRowCount, emptyMasterItem);
    const newIds = expanded.map(item => item.id);
    const newGrid = itemsToGridData(expanded);
    itemIdsRef.current = newIds;
    setItemIds(newIds);
    setTableData(newGrid);
    hot?.loadData(newGrid);
    hot?.render();
  };

  const handleRemoveItemSlot = useCallback((visualRow: number, col: number) => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;

    const slot = slotFromCol(col);
    const data: string[][] = hot.getData();
    const visualRowCount = data.length;
    const items = flattenGridToItems(data, itemIdsRef.current);
    const seqIdx = sequentialIdxFromVisual(visualRow, slot, visualRowCount);
    const removed = items[seqIdx];

    if (!removed?.value && !removed?.id) return;

    if (removed.id) deletedIdsRef.current.add(removed.id);
    items.splice(seqIdx, 1);

    const newGrid = itemsToGridData(items);
    const newIds = itemIdsFromItems(items);
    itemIdsRef.current = newIds;
    setItemIds(newIds);
    setTableData(newGrid);
    hot.loadData(newGrid);
    hot.render();
  }, []);

  const handleCreateRow = (index: number, amount: number) => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const newData: string[][] = hot.getData();
    const oldData = tableData;
    const oldIds = itemIdsRef.current;
    const newIds = remapIdsColumnMajor(oldData, oldIds, newData);
    itemIdsRef.current = newIds;
    setItemIds(newIds);
    setTableData(newData);
    hot.render();
  };

  return (
    <div
      style={{
        flex: '1 1 0',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <p style={{ color: '#666', fontSize: 14, margin: '0 0 12px' }}>
        {valueHeader} 목록을 관리합니다. 값을 Delete 키로 지우거나 셀에서 우클릭 → 항목 삭제 후 저장하면 삭제됩니다.
        엑셀/구글시트에서 복사해 <b>셀을 한 번 클릭(파란 테두리)</b> 후 <b>Ctrl+V</b>로 붙여넣을 수 있습니다.
      </p>
      <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          onClick={handleAddRow}
          style={{ padding: '6px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
        >
          + 행 추가
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || loading}
          style={{ padding: '6px 16px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
        >
          {saving ? '저장 중…' : '저장'}
        </button>
        <button
          type="button"
          onClick={loadRows}
          style={{ padding: '6px 16px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
        >
          새로고침
        </button>
      </div>
      {message && <div style={{ color: '#2e7d32', marginBottom: 8 }}>{message}</div>}
      {loading ? (
        <div style={{ padding: 24, color: '#888' }}>불러오는 중…</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <style>{`
            .container-master-grid .htCore td.container-master-no {
              background: #f5f7f9 !important;
              color: #546e7a;
              font-weight: 600;
              text-align: center;
            }
            .container-master-grid .htCore th.container-master-no {
              background: #eceff1 !important;
              text-align: center;
            }
            .container-master-grid .htCore td.container-master-slot-end {
              border-right: 2px solid #cfd8dc !important;
            }
            .container-master-grid .htCore th.container-master-slot-end {
              border-right: 2px solid #b0bec5 !important;
            }
          `}</style>
          <HotTable
            ref={hotRef}
            className="container-master-grid"
            data={tableData}
            colHeaders={buildHeaders(valueHeader)}
            rowHeaders={false}
            width="100%"
            height="100%"
            licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
            stretchH="all"
            colWidths={[44, undefined, undefined, 44, undefined, undefined]}
            copyPaste
            contextMenu={{
              items: {
                remove_item: {
                  name: '항목 삭제',
                  callback(this: Handsontable) {
                    const sel = this.getSelectedLast();
                    if (!sel) return;
                    const [row, col] = sel;
                    handleRemoveItemSlot(row, col);
                  },
                  disabled(this: Handsontable) {
                    const sel = this.getSelectedLast();
                    if (!sel) return true;
                    const [row, col] = sel;
                    if (isNumberCol(col)) return true;
                    const slot = slotFromCol(col);
                    const value = String(this.getDataAtCell(row, valueCol(slot)) || '').trim();
                    const seqIdx = sequentialIdxFromVisual(row, slot, this.countRows());
                    const id = itemIdsRef.current[seqIdx];
                    return !value && !id;
                  },
                },
              },
            }}
            beforeRemoveRow={() => false}
            afterChange={(changes, source) => {
              if (source === 'loadData' || !changes) return;
              const hot = hotRef.current?.hotInstance;
              if (hot) {
                for (const [row, col] of changes) {
                  if (typeof col === 'number' && isNumberCol(col)) {
                    hot.setDataAtCell(row as number, col, '', 'loadData');
                  }
                }
              }
              syncTableData();
            }}
            afterCreateRow={(index, amount) => {
              handleCreateRow(index, amount);
              hotRef.current?.hotInstance?.render();
            }}
            afterGetColHeader={(col, TH) => {
              if (isNumberCol(col)) {
                TH.classList.add('container-master-no');
              } else if (col === memoCol(0)) {
                TH.classList.add('container-master-slot-end');
              }
            }}
            cells={(row, col) => {
              const props: Handsontable.CellMeta = {};
              if (isNumberCol(col)) {
                props.readOnly = true;
                props.className = 'container-master-no';
                props.renderer = itemNumberRenderer;
              } else if (col === memoCol(0)) {
                props.className = 'container-master-slot-end';
              }
              return props;
            }}
            columns={Array.from({ length: GRID_COL_COUNT }, (_, col) => (
              isNumberCol(col)
                ? { type: 'text' as const, readOnly: true, renderer: itemNumberRenderer }
                : { type: 'text' as const }
            ))}
          />
        </div>
      )}
    </div>
  );
};

export default ContainerMasterGrid;
